import { useEffect, type CSSProperties } from 'react';
import { CheckCircle2, CircleHelp, CircleX, Info, Loader2, RotateCw } from 'lucide-react';
import toast, { ToastBar, Toaster, type Toast } from 'react-hot-toast';

// Uygulamadaki tüm bildirimler aynı görünümde: üst ortadan aşağı kayarak açılır, yukarı kayarak kapanır.
// Bilgi, başarı ve hata bildirimleri 3 sn sonra kendiliğinden kapanır; onaylar cevap verilene kadar açık kalır.
const AUTO_CLOSE_MS = 3000;

const TOAST_STYLE: CSSProperties = {
  background: '#ffffff',
  color: '#0f172a',
  border: '1px solid #e2e8f0',
  borderRadius: 14,
  boxShadow: '0 12px 32px -12px rgba(15, 23, 42, 0.25)',
  padding: '10px 14px',
  fontSize: 13,
  maxWidth: 440,
};

function ToastIcon({ type }: { type: Toast['type'] }) {
  const cls = 'h-5 w-5 shrink-0 text-blue-600';
  if (type === 'success') return <CheckCircle2 className={cls} aria-hidden="true" />;
  // Hata, bilgiyle karışmasın diye kırmızı çarpı ikonuyla gösterilir; bildirim kutusu yine aynı renktir.
  if (type === 'error') return <CircleX className="h-5 w-5 shrink-0 text-rose-600" aria-hidden="true" />;
  if (type === 'loading') return <Loader2 className={`${cls} animate-spin`} aria-hidden="true" />;
  return <Info className={cls} aria-hidden="true" />;
}

export function AppToaster() {
  return (
    <Toaster
      position="top-center"
      containerStyle={{ zIndex: 99999 }}
      toastOptions={{
        style: TOAST_STYLE,
        success: { duration: AUTO_CLOSE_MS },
        error: { duration: AUTO_CLOSE_MS },
        blank: { duration: AUTO_CLOSE_MS },
      }}
    >
      {(t) => (
        <ToastBar toast={t} style={TOAST_STYLE}>
          {({ message }) => (
            <div className="flex items-start gap-2.5">
              {t.icon ? <span className="mt-0.5 shrink-0">{t.icon}</span> : <span className="mt-0.5"><ToastIcon type={t.type} /></span>}
              <div className="min-w-0 flex-1 [&>div]:m-0 [&>div]:justify-start [&>div]:text-left">{message}</div>
            </div>
          )}
        </ToastBar>
      )}
    </Toaster>
  );
}

type ConfirmOptions = {
  /** Kısa onay butonu metni: "Evet", "Tamam" vb. */
  confirmLabel?: string;
  /** Kısa vazgeç butonu metni: "Hayır", "İptal" vb. */
  cancelLabel?: string;
};

/** Üst ortada Evet/Hayır soran bildirim; kullanıcı seçene kadar açık kalır. */
export function confirmToast(message: string, detail?: string | null, { confirmLabel = 'Evet', cancelLabel = 'Hayır' }: ConfirmOptions = {}): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (id: string, value: boolean) => {
      if (settled) return;
      settled = true;
      toast.dismiss(id);
      resolve(value);
    };
    toast(
      (t) => (
        <div className="flex min-w-[240px] flex-col gap-2.5" role="alertdialog" aria-label={message}>
          <div>
            <p className="text-sm font-semibold text-slate-900">{message}</p>
            {detail && <p className="mt-0.5 text-xs text-slate-500">{detail}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => finish(t.id, false)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              autoFocus
              onClick={() => finish(t.id, true)}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700"
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      ),
      { duration: Infinity, icon: <CircleHelp className="h-5 w-5 text-blue-600" aria-hidden="true" /> },
    );
  });
}


/** Sayfa içinde tutulan hata metnini, değiştiğinde üst ortada hata bildirimi olarak gösterir. */
export function useErrorToast(message?: string | null) {
  useEffect(() => {
    if (message) toast.error(message, { id: `error:${message}` });
  }, [message]);
}

/** Veri yüklenemediğinde sayfa ortasında hata metni yerine yalnızca tekrar deneme butonu. */
export function LoadRetry({ onRetry, className = '' }: { onRetry?: () => void; className?: string }) {
  return (
    <div className={`flex items-center justify-center p-6 ${className}`}>
      <button
        type="button"
        onClick={onRetry ?? (() => window.location.reload())}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
      >
        <RotateCw size={14} aria-hidden="true" />Tekrar dene
      </button>
    </div>
  );
}
